import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { teamsApi, gamesApi, usersApi, teamMembersApi } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { PlusIcon, EditIcon, TrashIcon, UsersIcon, UserMinusIcon, CloseIcon } from '../Icons/Icons';
import './TeamsList.css';

const MoreIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/>
  </svg>
);

const ArrowLeftIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
  </svg>
);

const GAME_ACCENTS = {
  'Valorant':          { color: '#ff4655', bg: 'rgba(255,70,85,0.12)',  border: 'rgba(255,70,85,0.25)' },
  'League of Legends': { color: '#c89b3c', bg: 'rgba(200,155,60,0.12)', border: 'rgba(200,155,60,0.25)' },
  'CS2':               { color: '#53ffab', bg: 'rgba(83,255,171,0.12)', border: 'rgba(83,255,171,0.25)' },
  'Counter-Strike 2':  { color: '#53ffab', bg: 'rgba(83,255,171,0.12)', border: 'rgba(83,255,171,0.25)' },
  'Rocket League':     { color: '#468dff', bg: 'rgba(70,141,255,0.12)', border: 'rgba(70,141,255,0.25)' },
};
const DEFAULT_ACCENT = { color: '#d2bcff', bg: 'rgba(135,70,255,0.12)', border: 'rgba(210,188,255,0.25)' };

const getAccent = (gameName) => GAME_ACCENTS[gameName] || DEFAULT_ACCENT;

const TeamsList = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const canCreate   = hasPermission('teams.create');
  const canEdit     = hasPermission('teams.edit');
  const canDelete   = hasPermission('teams.delete');
  const canManage   = hasPermission('teams.manage_members');

  const [teams, setTeams] = useState([]);
  const [games, setGames] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [sortBy, setSortBy] = useState('name');
  const [searchQuery, setSearchQuery] = useState('');
  const [gameFilter, setGameFilter] = useState('');

  // Create / Edit modal
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [formData, setFormData] = useState({ name: '', game_id: '', coach: '' });

  // Member management modal
  const [managingTeamId, setManagingTeamId] = useState(null);
  const [teamUsers, setTeamUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');

  // Dropdown menu
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    loadData();
  }, [sortBy, searchQuery]);

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenuId(null);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [t, g, u] = await Promise.all([
        teamsApi.getWithStats({ sort_by: sortBy, search: searchQuery || undefined }),
        gamesApi.getAll(),
        usersApi.getAll(),
      ]);
      setTeams(t.data);
      setGames(g.data);
      setAllUsers(u.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getGameName = (gameId) => games.find(g => g.id === gameId)?.name || 'N/A';

  const filteredTeams = teams.filter(t => !gameFilter || t.game_id === parseInt(gameFilter));

  // ── Create / Edit ──────────────────────────────────────────────
  const openCreate = () => {
    setEditingTeam(null);
    setFormData({ name: '', game_id: '', coach: '' });
    setShowFormModal(true);
  };

  const openEdit = (team, e) => {
    e.stopPropagation();
    setOpenMenuId(null);
    setEditingTeam(team);
    setFormData({ name: team.name, game_id: team.game_id, coach: team.coach || '' });
    setShowFormModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { name: formData.name, game_id: parseInt(formData.game_id), coach: formData.coach || null };
      if (editingTeam) await teamsApi.update(editingTeam.id, data);
      else await teamsApi.create(data);
      setShowFormModal(false);
      loadData();
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    setOpenMenuId(null);
    if (!window.confirm('Supprimer cette équipe ?')) return;
    try { await teamsApi.delete(id); loadData(); } catch (err) { console.error(err); }
  };

  // ── Member management ──────────────────────────────────────────
  const openManage = async (teamId, e) => {
    e.stopPropagation();
    setOpenMenuId(null);
    setManagingTeamId(teamId);
    await refreshMembers(teamId);
  };

  const refreshMembers = async (teamId) => {
    const [membersRes, usersRes] = await Promise.all([
      teamMembersApi.getTeamMembers(teamId),
      usersApi.getAll(),
    ]);
    const members = membersRes.data;
    const memberMap = {};
    members.forEach(m => { memberMap[m.user_id] = { mmr: m.mmr, mmr_active: m.mmr_active }; });
    const memberIds = members.map(m => m.user_id);
    setAllUsers(usersRes.data);
    setTeamUsers(usersRes.data.filter(u => memberIds.includes(u.id) && u.role !== 'admin').map(u => ({ ...u, ...memberMap[u.id] })));
    setAvailableUsers(usersRes.data.filter(u => !memberIds.includes(u.id) && u.role !== 'admin'));
  };

  const addPlayer = async (userId) => {
    await teamMembersApi.addMember(managingTeamId, userId, null);
    await refreshMembers(managingTeamId);
    const t = await teamsApi.getWithStats({ sort_by: sortBy });
    setTeams(t.data);
  };

  const removePlayer = async (userId) => {
    await teamMembersApi.removeMember(managingTeamId, userId);
    await refreshMembers(managingTeamId);
    const t = await teamsApi.getWithStats({ sort_by: sortBy });
    setTeams(t.data);
  };

  const updateMMR = async (userId, mmr, mmrActive) => {
    await teamMembersApi.updateMemberMMR(managingTeamId, userId, mmr, mmrActive);
    setTeamUsers(prev => prev.map(u => u.id === userId ? { ...u, mmr, mmr_active: mmrActive } : u));
    const t = await teamsApi.getWithStats({ sort_by: sortBy });
    setTeams(t.data);
  };

  // ── Placeholder avatar dots ────────────────────────────────────
  const AVATAR_COLORS = ['#8746ff', '#53ffab', '#ffb783', '#468dff', '#ff7070', '#d2bcff'];
  const renderAvatarStack = (team) => {
    const count = team.player_count || 0;
    const visible = Math.min(count, 3);
    const seed = team.name.charCodeAt(0);
    return (
      <div className="tl-avatar-stack">
        {Array.from({ length: visible }).map((_, i) => (
          <div key={i} className="tl-avatar-dot" style={{ background: AVATAR_COLORS[(seed + i) % AVATAR_COLORS.length] }}>
            {team.name.charAt(i % team.name.length).toUpperCase()}
          </div>
        ))}
        {count > 3 && <div className="tl-avatar-dot tl-avatar-more">+{count - 3}</div>}
        {count === 0 && <span className="tl-no-members">Aucun membre</span>}
      </div>
    );
  };

  return (
    <div className="tl-page">
      {/* ── Header ─────────────────────────────────────── */}
      <div className="tl-header">
        <div>
          <h1 className="tl-title">Équipes</h1>
          <p className="tl-subtitle">Gérez et suivez toutes les équipes compétitives.</p>
        </div>
        <div className="tl-header-actions">
          <div className="tl-search-wrap">
            <svg className="tl-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input
              className="tl-search-input"
              placeholder="Rechercher une équipe…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
          {canCreate && (
            <button className="tl-btn-create" onClick={openCreate}>
              <PlusIcon size={16} /> Créer une équipe
            </button>
          )}
        </div>
      </div>

      {/* ── Filter pills ───────────────────────────────── */}
      <div className="tl-filter-pills">
        <button
          className={`tl-pill${!gameFilter ? ' tl-pill--active' : ''}`}
          onClick={() => setGameFilter('')}
        >
          Tous les jeux
        </button>
        {games.map(game => (
          <button
            key={game.id}
            className={`tl-pill${gameFilter === String(game.id) ? ' tl-pill--active' : ''}`}
            onClick={() => setGameFilter(gameFilter === String(game.id) ? '' : String(game.id))}
          >
            {game.name}
          </button>
        ))}
        <select
          className="tl-sort-select"
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
        >
          <option value="name">Trier : Nom</option>
          <option value="mmr">Trier : MMR</option>
        </select>
      </div>

      {/* ── Grid ───────────────────────────────────────── */}
      {loading ? (
        <p className="tl-empty">Chargement…</p>
      ) : (
        <div className="tl-grid">
          {filteredTeams.map(team => {
            const gameName = getGameName(team.game_id);
            const accent = getAccent(gameName);
            const isMenuOpen = openMenuId === team.id;

            return (
              <div
                key={team.id}
                className="tl-card"
                onClick={() => navigate(`/equipes/${team.id}`)}
              >
                {/* Hover glow overlay */}
                <div className="tl-card-glow" style={{ '--accent': accent.color }} />

                {/* Top row */}
                <div className="tl-card-top">
                  <div className="tl-team-logo" style={{ borderColor: accent.border, color: accent.color, background: accent.bg }}>
                    {team.name.charAt(0).toUpperCase()}
                  </div>
                  <span className="tl-status-badge">
                    <span className="tl-status-dot" />
                    Actif
                  </span>
                </div>

                {/* Info */}
                <div className="tl-card-info">
                  <div className="tl-game-label" style={{ color: accent.color }}>
                    {gameName.toUpperCase()}{team.coach ? ` · ${team.coach}` : ''}
                  </div>
                  <h3 className="tl-team-name">{team.name}</h3>
                  <div className="tl-team-meta">
                    <UsersIcon size={14} />
                    <span>{team.player_count || 0} joueur{(team.player_count || 0) !== 1 ? 's' : ''}</span>
                    {team.average_mmr && (
                      <>
                        <span className="tl-meta-sep">·</span>
                        <span style={{ color: accent.color, fontWeight: 700 }}>
                          MMR {Math.round(team.average_mmr)}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="tl-card-footer">
                  {renderAvatarStack(team)}

                  {(canEdit || canDelete || canManage) && (
                    <div className="tl-menu-wrap" ref={isMenuOpen ? menuRef : null}>
                      <button
                        className="tl-menu-btn"
                        onClick={e => { e.stopPropagation(); setOpenMenuId(isMenuOpen ? null : team.id); }}
                      >
                        <MoreIcon />
                      </button>
                      {isMenuOpen && (
                        <div className="tl-dropdown">
                          {canEdit && (
                            <button className="tl-dropdown-item" onClick={e => openEdit(team, e)}>
                              <EditIcon size={14} /> Modifier
                            </button>
                          )}
                          {canManage && (
                            <button className="tl-dropdown-item" onClick={e => openManage(team.id, e)}>
                              <UsersIcon size={14} /> Gérer les membres
                            </button>
                          )}
                          <button className="tl-dropdown-item" onClick={e => { e.stopPropagation(); setOpenMenuId(null); navigate(`/equipes/${team.id}/statistiques`); }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                            Statistiques
                          </button>
                          {canDelete && (
                            <button className="tl-dropdown-item tl-dropdown-item--danger" onClick={e => handleDelete(team.id, e)}>
                              <TrashIcon size={14} /> Supprimer
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Create card */}
          {canCreate && (
            <button className="tl-card tl-card--create" onClick={openCreate}>
              <div className="tl-create-icon"><PlusIcon size={28} /></div>
              <h3 className="tl-create-label">Créer une équipe</h3>
              <p className="tl-create-sub">Ajouter une nouvelle division</p>
            </button>
          )}
        </div>
      )}

      {/* ── Create / Edit Modal ────────────────────────── */}
      {showFormModal && (
        <div className="tl-modal-overlay" onClick={() => setShowFormModal(false)}>
          <div className="tl-modal" onClick={e => e.stopPropagation()}>
            <button className="tl-modal-close" onClick={() => setShowFormModal(false)}><CloseIcon size={16} /></button>
            <h2>{editingTeam ? 'Modifier l\'équipe' : 'Nouvelle équipe'}</h2>
            <form onSubmit={handleSubmit} className="tl-form">
              <div className="tl-form-group">
                <label>Nom de l'équipe</label>
                <input
                  type="text"
                  placeholder="TSK Valorant Main…"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="tl-form-group">
                <label>Jeu</label>
                <select
                  value={formData.game_id}
                  onChange={e => setFormData({ ...formData, game_id: e.target.value })}
                  required
                >
                  <option value="">Sélectionner un jeu</option>
                  {games.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </div>
              <div className="tl-form-group">
                <label>Manager</label>
                <select
                  value={formData.coach}
                  onChange={e => setFormData({ ...formData, coach: e.target.value })}
                >
                  <option value="">Aucun manager</option>
                  {allUsers
                    .filter(u => ['manager', 'responsable', 'ceo', 'coo'].includes(u.role))
                    .map(u => <option key={u.id} value={u.username}>{u.username}</option>)}
                </select>
              </div>
              <div className="tl-form-actions">
                <button type="button" className="tl-btn-cancel" onClick={() => setShowFormModal(false)}>Annuler</button>
                <button type="submit" className="tl-btn-submit">{editingTeam ? 'Enregistrer' : 'Créer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Member Management Modal ────────────────────── */}
      {managingTeamId && (
        <div className="tl-modal-overlay" onClick={() => setManagingTeamId(null)}>
          <div className="tl-modal tl-modal--wide" onClick={e => e.stopPropagation()}>
            <button className="tl-modal-close" onClick={() => setManagingTeamId(null)}><CloseIcon size={16} /></button>
            <h2><UsersIcon size={18} /> Membres — {teams.find(t => t.id === managingTeamId)?.name}</h2>
            <input
              className="tl-member-search"
              placeholder="Rechercher…"
              value={memberSearch}
              onChange={e => setMemberSearch(e.target.value)}
            />
            <div className="tl-members-grid">
              <div className="tl-member-col">
                <h4>Dans l'équipe ({teamUsers.filter(u => !memberSearch || u.username.toLowerCase().includes(memberSearch.toLowerCase())).length})</h4>
                <div className="tl-member-list">
                  {teamUsers
                    .filter(u => !memberSearch || u.username.toLowerCase().includes(memberSearch.toLowerCase()))
                    .map(u => (
                      <div key={u.id} className="tl-member-item">
                        <div className="tl-member-avatar">{u.username.charAt(0).toUpperCase()}</div>
                        <div className="tl-member-info">
                          <span className="tl-member-name">{u.username}</span>
                          <span className="tl-member-role">{u.role}</span>
                        </div>
                        <label className="tl-mmr-toggle">
                          <input
                            type="checkbox"
                            checked={u.mmr_active ?? true}
                            onChange={e => updateMMR(u.id, u.mmr, e.target.checked)}
                          />
                          <span>MMR</span>
                        </label>
                        <button className="tl-btn-remove" onClick={() => removePlayer(u.id)}>
                          <UserMinusIcon size={14} />
                        </button>
                      </div>
                    ))}
                </div>
              </div>
              <div className="tl-member-col">
                <h4>Disponibles ({availableUsers.filter(u => !memberSearch || u.username.toLowerCase().includes(memberSearch.toLowerCase())).length})</h4>
                <div className="tl-member-list">
                  {availableUsers
                    .filter(u => !memberSearch || u.username.toLowerCase().includes(memberSearch.toLowerCase()))
                    .map(u => (
                      <div key={u.id} className="tl-member-item">
                        <div className="tl-member-avatar tl-member-avatar--muted">{u.username.charAt(0).toUpperCase()}</div>
                        <div className="tl-member-info">
                          <span className="tl-member-name">{u.username}</span>
                          <span className="tl-member-role">{u.role}</span>
                        </div>
                        <button className="tl-btn-add" onClick={() => addPlayer(u.id)}>+</button>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamsList;
